import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Linkedin, ChevronDown } from 'lucide-react';
import { TeamMember } from './types';

interface TeamMemberCardProps {
  member: TeamMember;
}

export function TeamMemberCard({ member }: TeamMemberCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow">
      {/* Image container */}
      <div className="w-full h-[280px]">
        <img
          className="w-full h-full object-cover object-top rounded-t-xl"
          src={member.image}
          alt={member.name}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'https://via.placeholder.com/280x280';
          }}
        />
      </div>

      {/* Content container */}
      <div className="p-4">
        {/* Header */}
        <div className="text-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
          <p className="text-green-600 font-medium mt-1 text-sm">{member.role}</p>
        </div>

        {/* Bio */}
        <div className="min-h-[3em]">
          <AnimatePresence mode="wait">
            <motion.div
              key={isExpanded ? 'expanded' : 'collapsed'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-gray-600 text-sm"
            >
              {isExpanded ? (
                member.bio
              ) : (
                <div className="line-clamp-2">{member.bio}</div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex space-x-3">
              {member.social.linkedin && (
                <a
                  href={member.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
            </div>
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm"
            >
              {isExpanded ? 'Show Less' : 'Read More'}
              <ChevronDown 
                className={`h-4 w-4 transition-transform ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}